import { CONSTANTS, getSeason, calculateDemand, applyProductionLoss, applyGridLoss } from './constants.js';
import StorageManager from './StorageManager.js';
import PriorityManager from './PriorityManager.js';
import ForecastManager from './ForecastManager.js';
import ImprovedDemandManager from './ImprovedDemandManager.js';
import ThermalPhysics from './ThermalPhysics.js';
import BatteryOptimizer from './BatteryOptimizer.js';

// Hovedsimulering med modulær struktur
export class SimulationEngineV2 {
  constructor(config) {
    this.config = config;
    this.currentHour = 0;
    
    // Initialize managers
    this.storage = new StorageManager(config);
    this.thermalPhysics = new ThermalPhysics();
    // Apply configurable turbine min inlet temp if provided
    if (typeof config.turbine_min_inlet_temp_c === 'number') {
      this.thermalPhysics.specs.turbine.minInletTemp = config.turbine_min_inlet_temp_c;
    }

    // Initialize thermal state from storage
    const levels = this.storage.getLevels();
    const htesCapacity = config.htes_capacity_MWh || (205 * (config.htes_modules || 1));
    const ltesCapacity = config.ltes_capacity_MWh || config.ltes_capacity || 205;
    this.thermalPhysics.initializeFromStorage(levels.htes, levels.ltes, htesCapacity, ltesCapacity);

    this.constraints = {
      heater_max_MW: config.heater_MW || 30,  // Bind to configurable heater MW (default 30)
      turbine_max_MW: config.turbine_MW || 10,
      htes_max_discharge_MW: config.turbine_MW || 10, // HTES discharge matches turbine capacity
      battery_max_charge_MW: config.battery_MW || 3,  // Configurable battery charge rate (1-10 MW)
      battery_max_discharge_MW: config.battery_MW || 3, // Configurable battery discharge rate (1-10 MW)
      battery_capacity_MWh: config.battery_MWh || (config.battery_MW || 3) // Battery capacity (default 1 hour storage)
    };
    this.priority = new PriorityManager(this.storage, this.constraints);
    this.forecast = new ForecastManager();
    // Inject forecast ref så DM kan læse strategi
    this.priority.forecast = this.forecast;
    this.demand = new ImprovedDemandManager(this.storage, this.priority, config);
    // Inject thermal physics reference
    this.demand.thermalPhysics = this.thermalPhysics;
    // Initialize battery optimizer
    this.batteryOptimizer = new BatteryOptimizer();
    
    // Results tracking
    this.results = this.initializeResults();

    // Data provider for forecast
    this.dataProvider = null;

    // HTES Arbitrage tracking - for electricity + heat production via turbine
    this.htesArbitrageTracking = {
      totalChargeMWh: 0,
      totalChargeCost: 0,
      totalDischargeMWh: 0,  // El-produktion via turbine
      totalDischargeRevenue: 0,
      totalHeatProduced: 0,  // Varme til LTES fra turbine
      totalHeatSavings: 0,   // Besparelse på varme
      chargeHours: [],  // Track purchase prices for avg calculation
      dischargeHours: [],  // Track sale prices for avg calculation
      cycleCount: 0,  // Number of complete charge/discharge cycles
      currentCycleCharge: 0,  // Tracking current cycle
    };
    
    // Battery cycle tracking - ENHANCED for multi-cycle
    this.batteryDailyTracking = {
      lastChargeDay: -1,
      lastDischargeDay: -1,
      totalChargeCycles: 0,
      totalDischargeCycles: 0,
      totalChargedMWh: 0,
      totalDischargedMWh: 0,
      dailyChargeRecord: new Array(366).fill(false), // Track each day's charge
      dailyDischargeRecord: new Array(366).fill(false), // Track each day's discharge
      dailyCycleCount: new Array(366).fill(0), // Track cycles per day for multi-cycle
      todayPrices: [], // Cache dagens priser for cycle beslutninger
      // Track up to 4 cycles per day
      cycle1Charged: new Array(366).fill(false),
      cycle1Discharged: new Array(366).fill(false),
      cycle2Charged: new Array(366).fill(false),
      cycle2Discharged: new Array(366).fill(false),
      cycle3Charged: new Array(366).fill(false),
      cycle3Discharged: new Array(366).fill(false),
      cycle4Charged: new Array(366).fill(false),
      cycle4Discharged: new Array(366).fill(false)
    };
    
    // Monthly heater availability tracking for balance market
    this.monthlyHeaterTracking = {
      // Track hours where heater was available (not used)
      monthlyAvailableHours: new Array(12).fill(0),
      // Track hours where heater was used
      monthlyUsedHours: new Array(12).fill(0),
      // Track total heater capacity used per month (MWh)
      monthlyUsedCapacity: new Array(12).fill(0)
    };
  }
  
  initializeResults() {
    return {
      // OPERATIONAL Indkøb (An/Ab værk) - ONLY energy actually used for An-værk production
      operationalGridPurchased: 0,
      operationalGridCost: 0,
      operationalSolarPurchased: 0,
      operationalSolarCost: 0,
      
      // Track An-værk electricity input separately (excluding waste heat)
      anPlantElectricityInput: 0,  // Electricity that actually went to An-værk production
      anPlantSolarInput: 0,         // Solar that actually went to An-værk production
      anPlantGridInput: 0,          // Grid that actually went to An-værk production
      
      // ARBITRAGE (separat tracking)
      arbitrageGridPurchased: 0,
      arbitrageGridCost: 0,
      arbitrageSolarPurchased: 0,
      arbitrageSolarCost: 0,
      arbitrageElectricitySold: 0,
      arbitrageElectricityRevenue: 0,
      
      // Operational factory waste heat (renamed for clarity)
      operationalFactoryWasteHeatUsed: 0,
      operationalFactoryWasteHeatCost: 0,
      solarAvailable: 0,
      wasteHeatAvailable: 0,  // ADD: Track total available waste heat
      wasteHeatAccepted: 0,   // ADD: Track accepted waste heat
      
      // Production
      electricityProduced: 0,
      electricitySold: 0,
      electricityRevenue: 0,
      batteryArbitrageRevenue: 0,
      
      // Heat delivery
      anPlantProduction: 0,
      productionLosses: 0,
      abPlantDelivery: 0,
      gridLosses: 0,
      netDelivery: 0,
      // REMOVED - use An/Ab/Net delivery instead
      
      // Total energy purchases for UI
      electricityPurchased: 0,  // ADD: Total electricity for UI
      solarPurchased: 0,         // ADD: Total solar for UI
      
      // Costs - only operational (not arbitrage)
      totalOperationalCost: 0,
      totalOpexCost: 0,
      consumedInitialInventoryValueDKK: 0,
      
      // Counters
      fullPurchaseHours: 0,
      partialPurchaseHours: 0,
      negativeHoursUsed: 0,
      negativeHoursAvailable: 0,
      turbineOperatingHours: 0,
      turbinePartialHours: 0, // Track partial hours based on MW output
      turbineElectricityProduced: 0, // Track turbine electricity separately
      turbineRevenue: 0, // Track turbine revenue separately
      batteryChargeAmount: 0,
      batteryChargeCost: 0,        // SEPARAT tracking af batteri ladning cost
      batteryDischargeAmount: 0,
      batteryLosses: 0,
      batteryElectricitySold: 0, // Track battery electricity separately
      batteryRevenue: 0, // Track battery revenue separately
      // Battery system services
      batteryServiceRevenue: 0,
      batteryServiceHours: 0,
      // Turbine system services
      turbineServiceRevenue: 0,
      turbineServiceHours: 0,

      // HTES Total tracking (HELE lageret)
      htesTotalCharged: 0,           // Alt der lades i HTES (operational + arbitrage)
      htesTotalChargeCost: 0,        // Total kostpris for HTES ladning
      htesTotalDischarged: 0,        // Alt der bruges fra HTES via turbine
      htesTotalElProduced: 0,        // Total el fra HTES via turbine
      htesTotalHeatProduced: 0,      // Total varme fra HTES via turbine
      htesTotalElRevenue: 0,         // Total indtægt fra el-salg

      // HTES Operational (via DemandManager)
      htesOperationalCharged: 0,
      htesOperationalChargeCost: 0,
      htesOperationalDischarged: 0,
      htesOperationalElProduced: 0,
      htesOperationalHeatProduced: 0,

      // HTES Arbitrage (strategisk køb/salg ved høje priser)
      htesArbitrageChargeMWh: 0,
      htesArbitrageChargeCost: 0,
      htesArbitrageElectricityProduced: 0,
      htesArbitrageRevenue: 0,
      htesArbitrageHeatProduced: 0,
      htesArbitrageHeatSavings: 0,
      htesArbitrageCycles: 0,

      // Hour data
      hourlyData: []
    };
  }
  
  // Set data provider for forecast
  setDataProvider(provider) {
    this.dataProvider = provider;
  }
  
  simulateHour(apiData) {
    // PERFORMANCE: Only log every 1000 hours to avoid browser slowdown
    if (this.currentHour % 1000 === 0) {
      console.log(`🎯 SimulationEngineV2.simulateHour - hour ${this.currentHour}`, apiData);
    }
    
    // STOP hvis vi har nået maks timer
    if (this.currentHour >= CONSTANTS.SIMULATION.totalHours) {
      console.log('⏹️ Reached max hours');
      return null;
    }
    
    // Check if apiData is valid
    if (!apiData || apiData.spotPrice === undefined) {
      console.error(`❌ No API data available for hour ${this.currentHour}`);
      console.error('apiData:', apiData);
      // Increment hour counter even on error
      this.currentHour++;
      // Return empty hour data instead of null to prevent crashes
      const emptyHourData = {
        hour: this.currentHour - 1,
        timestamp: new Date().toISOString(),
        actions: [],
        costs: { raw: 0, revenue: 0, net: 0, opex: 0, cumulative_raw: 0, cumulative_total: 0 },
        storage: { 
          htes_level: this.storage.getLevels().htes,
          ltes_level: this.storage.getLevels().ltes,
          battery_level: this.storage.getLevels().battery
        },
        energyBalance: { production: 0, consumed: 0, stored: 0, unmet: 0 },
        losses: { production: 0, grid: 0 }
      };
      
      // Update cumulative totals even for empty hours
      this.results.cumulativeRawCost += 0;
      this.results.cumulativeTotalCost += 0;
      
      return emptyHourData;
    }
    
    const { spotPrice, solarMWh, demandMW, wasteHeatMW, month, day, hour } = apiData;
    const demands = calculateDemand(demandMW);
    
    // Load forecast for next 24 hours
    if (this.dataProvider && this.currentHour % 6 === 0) {
      this.forecast.loadForecast(this.currentHour, this.dataProvider);
    }
    
    // Get forecast recommendation
    const recommendation = this.forecast.getHourlyRecommendation(this.currentHour);
    
    // Reset hourly tracking
    this.priority.resetHourlyTracking();
    
    // Hour data object
    const hourData = {
      hour: this.currentHour,
      timestamp: apiData.timestamp,
      month,
      day,
      hourOfDay: hour,
      spotPrice,
      solarMWh,
      demandMW,
      wasteHeatMW,
      season: getSeason(month),
      anPlantDemand: demands.an,
      abPlantDemand: demands.ab,
      netDemand: demands.net,
      actions: [],
      storage: {},
      costs: {},
      losses: { production: 0, grid: 0, battery: 0 }
    };
    
    // Debug: Confirm hourData exists
    if (!hourData || !hourData.actions) {
      console.error('CRITICAL: hourData was not created properly!', hourData);
      return null;
    }
    
    let totalCost = 0;
    let totalRevenue = 0;
    let energyInputThisHour = 0;
    let energyOutputThisHour = 0;
    let anPlantProductionThisHour = 0;
    
    // Track available resources
    if (solarMWh > 0) {
      this.results.solarAvailable += solarMWh;
    }
    if (wasteHeatMW > 0) {
      this.results.wasteHeatAvailable += wasteHeatMW;
    }
    
    // Apply thermal losses before any operations
    this.thermalPhysics.applyThermalLosses(this.currentHour);

    // ========================================
    // 1. NEGATIVE PRISER - AGGRESSIV MULTI-CYCLE STRATEGI
    // ========================================
    if (spotPrice < 0) {
      this.results.negativeHoursAvailable++;
      const negativeRevenue = Math.abs(spotPrice); // Vi får BETALT for at købe!

      // Multi-cycle strategi: Fyld, kør turbine, fyld igen, etc.
      let totalNegativePurchase = 0;
      let totalTurbineEl = 0;
      const MAX_CYCLES = 3; // Max 3 cycles pr time for at undgå uendelig loop

      for (let cycle = 0; cycle < MAX_CYCLES; cycle++) {
        let space = this.storage.getAvailableSpace();
        const levels = this.storage.getLevels();

        // FASE 1: Fyld HTES fuld med negativ pris el
        space = this.storage.getAvailableSpace();
        if (space.htes > 1) {
          const htesCharge = Math.min(this.constraints.heater_max_MW, space.htes);
          if (htesCharge > 1) {
            const stored = this.storage.chargeHTES(htesCharge);
            this.thermalPhysics.updateHTESCharge(stored, this.currentHour);
            energyInputThisHour += htesCharge;
            totalRevenue += negativeRevenue * htesCharge;
            totalNegativePurchase += htesCharge;
            hourData.actions.push({
              type: 'CHARGE_HTES_NEGATIVE',
              amount: htesCharge,
              stored: stored,
              revenue: negativeRevenue * htesCharge,
              cycle: cycle + 1,
              reason: 'Fyld HTES ved negativ pris'
            });

            // KRITISK TRACKING: Negative pris HTES-køb er OPERATIONAL (går til varmeproduktion)
            this.results.operationalGridPurchased += htesCharge;
            this.results.electricityPurchased += htesCharge;
            this.results.htesOperationalCharged += stored;
            this.results.htesTotalCharged += stored;
            // Negativ pris = vi FÅR penge, så cost er negativ
            const negativeCost = negativeRevenue * htesCharge; // negativt tal (indtægt)
            this.results.operationalGridCost += negativeCost;
            this.results.htesOperationalChargeCost += negativeCost;
            this.results.htesTotalChargeCost += negativeCost;
          }
        }

        // FASE 3: Kør turbine PURE EL-MODE (ingen varme til demand!)
        // KRITISK: Varme må IKKE bidrage til operational demand - kun el-arbitrage!
        const htesLevel = this.storage.getLevels().htes;

        if (htesLevel > 0.20) {
          // Kør turbine med FULD kapacitet til 100% el produktion
          const turbineInput = Math.min(
            this.constraints.turbine_max_MW,
            htesLevel * this.storage.storage.htes.capacity
          );

          if (turbineInput > 1) {
            const discharged = this.storage.dischargeHTES(turbineInput);
            this.thermalPhysics.updateHTESDischarge(discharged, this.currentHour);

            // PURE EL-MODE: Max el output, spild resten (ingen varme til LTES!)
            // Dette sikrer turbine IKKE bidrager til operational demand
            // Ved 300°C HTES: ~20% el efficiency (max performance)
            const turbineEfficiency = this.thermalPhysics ?
              this.thermalPhysics.getTurbineEfficiency() :
              { heat: 0, electricity: 0.20 }; // 100% el-mode: ingen varme!

            const electricityOut = discharged * Math.max(turbineEfficiency.electricity, 0.15);
            // Ingen varme gemmes! Rest af energi er spild for ren el-arbitrage

            // Sælg el til grid - antag næste times pris eller minimum 100 DKK
            const elSalePrice = Math.max(100, negativeRevenue * 2); // Konservativ estimat
            const elRevenue = electricityOut * elSalePrice;

            totalRevenue += elRevenue;
            totalTurbineEl += electricityOut;
            // KRITISK: IKKE track her - det gøres i action-loopen for at undgå dobbelt-tælling!
            // this.results.turbineElectricityProduced, turbineRevenue, turbineOperatingHours
            // trackes når actionen behandles i demandResult.actions.forEach()

            hourData.actions.push({
              type: 'TURBINE_NEGATIVE_PRICE_ARBITRAGE',
              htesInput: discharged,
              electricityOut: electricityOut,
              heatOut: 0, // INGEN varme til LTES - pure el-mode
              heatStored: 0,
              revenue: elRevenue,
              elSalePrice: elSalePrice,
              cycle: cycle + 1,
              reason: `Pure El-Mode: ${electricityOut.toFixed(1)} MWh el til ${elSalePrice.toFixed(0)} DKK (ingen varme til demand)`
            });
          }
        }

        // Stop hvis vi ikke kan gøre mere
        space = this.storage.getAvailableSpace();
        if (space.htes < 1 && space.ltes < 1) break;
      }

      if (totalNegativePurchase > 0) {
        this.results.negativeHoursUsed++;
      }
    }

    // ========================================
    // 1B. HTES SMART STRATEGI - KØB BILLIGT, BRUG STRATEGISK
    // ========================================
    // Strategi:
    // 1. Køb billigt til HTES ved lave priser
    // 2. Demand dækkes først (via DemandManager)
    // 3. Ekstra HTES bruges til el-produktion ved høje priser (efter demand)

    if (spotPrice >= 0) {
      const htesLevels = this.storage.getLevels();
      const htesSpace = this.storage.getAvailableSpace();
      const ltesAvailable = htesLevels.ltes * this.storage.storage.ltes.capacity;
      const isSummer = month >= 5 && month <= 8;
      const isLowDemand = demands.net < 5; // Under 5 MWh varmebehov

      // HTES kapacitet: 300 MWh samlet (2x150MWh moduler)
      const htesCapacity = this.storage.storage.htes.capacity;
      const turbineMaxMW = this.constraints.turbine_max_MW || 20;

      // Hent forecast for at finde bedste købs- og salgstimer
      let forecastPrices = [];
      if (this.dataProvider) {
        const dayOfYear = Math.floor(this.currentHour / 24);
        const startHour = dayOfYear * 24;
        for (let h = 0; h < 24 && startHour + h < 8784; h++) {
          const hourIndex = startHour + h;
          let price = 0;
          if (typeof this.dataProvider === 'function') {
            const data = this.dataProvider(hourIndex);
            price = data ? data.spotPrice : 0;
          } else if (this.dataProvider && typeof this.dataProvider === 'object') {
            price = this.dataProvider.getSpotPrice ? this.dataProvider.getSpotPrice(hourIndex) : 0;
          }
          forecastPrices.push({ hour: h, price: price || 0 });
        }
      }

      // Find dagens billigste og dyreste timer
      const sortedByPrice = [...forecastPrices].sort((a, b) => a.price - b.price);
      const cheapestHours = sortedByPrice.slice(0, 6).map(p => p.hour); // 6 billigste timer
      const expensiveHours = sortedByPrice.slice(-6).map(p => p.hour); // 6 dyreste timer
      const minPrice = sortedByPrice.length > 0 ? sortedByPrice[0].price : spotPrice;
      const maxPrice = sortedByPrice.length > 0 ? sortedByPrice[sortedByPrice.length - 1].price : spotPrice;
      const potentialSpread = maxPrice - minPrice;

      // ARBITRAGE BESLUTNING baseret på 200 DKK minimum spread
      const MIN_ARBITRAGE_SPREAD = 200; // DKK/MWh minimum profit spread
      const isArbitrageDay = potentialSpread >= MIN_ARBITRAGE_SPREAD;

      // KØB TIL HTES: DEAKTIVERET - Al HTES charging styres nu af DemandManager
      // Dette sikrer at vi kun køber el til varmeproduktion, ikke spekulativ arbitrage
      // Arbitrage el-salg sker stadig via turbine når HTES afladesfor at dække demand
      const currentHourOfDay = this.currentHour % 24;
      const isChargeHour = cheapestHours.includes(currentHourOfDay);
      const DISABLE_HTES_SPECULATIVE_CHARGE = true; // DEAKTIVER spekulativ HTES ladning

      if (!DISABLE_HTES_SPECULATIVE_CHARGE && isArbitrageDay && isChargeHour && htesSpace.htes > 1 && spotPrice < 150) {
        // DEAKTIVERET - denne kode kører ikke
        // Al HTES charging håndteres af DemandManager.handleGridPurchase()
      }

      // SÆLG FRA HTES: DISABLED - Turbine styres af DemandManager!
      // Denne logik er flyttet til DemandManager for at undgå konflikt med operational demand
      const DISABLE_ARBITRAGE_DISCHARGE = true;

      const isDischargeHour = expensiveHours.includes(currentHourOfDay);
      const htesAvailable = htesLevels.htes * htesCapacity;
      const ltesSpace = htesSpace.ltes; // Plads i LTES til turbine varme

      if (!DISABLE_ARBITRAGE_DISCHARGE && isArbitrageDay && isDischargeHour && htesAvailable > turbineMaxMW && spotPrice > 250) {
        // DEAKTIVERET - denne kode kører ikke
        if (false) {
          // Beregn forventet profit INKL. varme-værdi
          const avgChargePrice = this.htesArbitrageTracking.totalChargeMWh > 0
            ? this.htesArbitrageTracking.totalChargeCost / this.htesArbitrageTracking.totalChargeMWh
            : 150; // Default antag 150 DKK

          // Turbine output: 15% el, 85% varme
          const turbineElEfficiency = 0.15;
          const turbineHeatEfficiency = 0.85;

          // Varme-værdi: Hvad ville vi ellers betale for varmen?
          // Gennemsnitspris for varme er ca. 200-300 DKK/MWh fra grid
          const heatValue = 200; // DKK/MWh - konservativ værdi for varme

          // Total værdi per MWh HTES input:
          // El: 15% * spotPrice + Varme: 85% * heatValue
          const totalValuePerMWhInput = (turbineElEfficiency * spotPrice) + (turbineHeatEfficiency * heatValue);
          const profitPerMWhInput = totalValuePerMWhInput - avgChargePrice;

          // Kun kør hvis vi tjener mindst 50 DKK per MWh input ELLER meget høj elpris
          if (profitPerMWhInput > 50 || spotPrice > 500) {
            // Kør turbine med fuld kapacitet
            // Begræns af: turbine kapacitet, HTES niveau, LTES plads til varme
            const maxByLTES = ltesSpace / turbineHeatEfficiency; // Max input baseret på LTES plads
            const turbineInput = Math.min(
              turbineMaxMW,
              htesAvailable - 10,
              maxByLTES > 0 ? maxByLTES : turbineMaxMW // Hvis LTES fuld, kør alligevel (varme spildes)
            );

            if (turbineInput > 1) {
              const discharged = this.storage.dischargeHTES(turbineInput);
              this.thermalPhysics.updateHTESDischarge(discharged, this.currentHour);

              // Turbine output
              const turbineEfficiency = this.thermalPhysics
                ? this.thermalPhysics.getTurbineEfficiency()
                : { heat: turbineHeatEfficiency, electricity: turbineElEfficiency };

              const electricityOut = discharged * Math.max(turbineEfficiency.electricity, 0.15);
              const heatOut = discharged * turbineEfficiency.heat;

              // VARME TIL LTES - ALTID gem det vi kan!
              let heatStored = 0;
              if (ltesSpace > 0.1) {
                heatStored = this.storage.chargeLTES(Math.min(heatOut, ltesSpace));
                this.thermalPhysics.updateLTESCharge(heatStored, this.currentHour);

                // KRITISK: Varme til LTES tæller IKKE som An-værk produktion her!
                // An-værk produktion tælles først når varmen BRUGES fra LTES til demand
                // (via DemandManager.deliverFromStorage → DISCHARGE_LTES_TO_DH)
                // Hvis vi tæller det her, får vi dobbelt-tælling!
                // anPlantProductionThisHour += heatStored;  // FJERNET - undgå dobbelt-tælling
              }

              const elRevenue = electricityOut * spotPrice;
              // Varme-værdi: Besparelse på ikke at købe el til varme
              const heatSavings = heatStored * heatValue;

              totalRevenue += elRevenue;
              // energyOutputThisHour er ikke An-værk output - det er LTES input
              // energyOutputThisHour += heatStored;  // FJERNET

              // Track HTES arbitrage separat
              this.htesArbitrageTracking.totalDischargeMWh += electricityOut;
              this.htesArbitrageTracking.totalDischargeRevenue += elRevenue;
              this.htesArbitrageTracking.totalHeatProduced += heatStored;
              this.htesArbitrageTracking.totalHeatSavings += heatSavings;
              this.htesArbitrageTracking.dischargeHours.push({ price: spotPrice, amount: electricityOut, heat: heatStored });

              // Track cycles
              if (this.htesArbitrageTracking.currentCycleCharge > 0) {
                this.htesArbitrageTracking.cycleCount++;
                this.htesArbitrageTracking.currentCycleCharge = 0;
              }

              this.results.htesArbitrageElectricityProduced += electricityOut;
              this.results.htesArbitrageRevenue += elRevenue;
              this.results.htesArbitrageHeatProduced += heatStored;
              this.results.htesArbitrageHeatSavings += heatSavings;
              this.results.turbineElectricityProduced += electricityOut;
              this.results.turbineRevenue += elRevenue;
              this.results.turbineOperatingHours++;

              // Profit beregning inkl. varme-værdi
              const totalValue = elRevenue + heatSavings;
              const inputCost = discharged * avgChargePrice;
              const actualProfit = totalValue - inputCost;

              hourData.actions.push({
                type: 'HTES_ARBITRAGE_DISCHARGE',
                htesInput: discharged,
                electricityOut: electricityOut,
                heatOut: heatOut,
                heatStored: heatStored,
                revenue: elRevenue,
                heatSavings: heatSavings,
                spotPrice: spotPrice,
                avgChargePrice: avgChargePrice,
                profit: actualProfit,
                reason: `HTES Arbitrage: ${electricityOut.toFixed(1)} MWh el @ ${spotPrice.toFixed(0)} DKK + ${heatStored.toFixed(1)} MWh varme (profit: ${actualProfit.toFixed(0)} DKK)`
              });
            }
          }
        }
      }
    }

    // ========================================
    // 2. BRUG DEMAND MANAGER FOR 100% COVERAGE
    // ========================================
    const demandResult = this.demand.meetDemand(demands.an, hour, spotPrice, wasteHeatMW, solarMWh, month, this.currentHour);
    hourData.actions.push(...demandResult.actions);
    
    // Track production - KUN An-produktion fra DemandManager!
    anPlantProductionThisHour = demandResult.anProduction;  // IKKE += , bare =
    // IKKE totalCost her - det trackes via individuelle actions nedenfor for at undgå dobbelt-tælling!
    totalRevenue += demandResult.totalRevenue;
    energyOutputThisHour = demandResult.anProduction;  // Samme som An-produktion
    
    // Calculate how much electricity was actually needed for An-værk production
    // An-værk production = LTES discharge, which came from: electricity + waste heat
    // We need to track how much electricity was used vs how much was waste heat
    let electricityForAnProduction = 0;
    let wasteHeatForProduction = 0;
    
    // Track monthly heater usage for balance market calculations
    const monthIndex = month - 1; // Convert to 0-based index
    const heaterCapacity = this.constraints.heater_max_MW || 45; // Use actual config value
    
    // Calculate total heater usage from all actions that use the heater
    let heaterUsedThisHour = 0;
    demandResult.actions.forEach(action => {
      // All these actions use the heater
      if (action.type === 'OP_CHARGE_LTES_SOLAR' || 
          action.type === 'OP_CHARGE_LTES_GRID' ||
          action.type === 'OP_CHARGE_HTES_SOLAR' ||
          action.type === 'OP_CHARGE_HTES_GRID') {
        heaterUsedThisHour += action.amount || 0;
      }
    });
    
    // Also check negative price heater usage
    hourData.actions.forEach(action => {
      if (action.type === 'CHARGE_HTES_NEGATIVE' || action.type === 'CHARGE_LTES_NEGATIVE') {
        heaterUsedThisHour += action.amount || 0;
      }
    });
    
    if (heaterUsedThisHour > 0.01) {
      this.monthlyHeaterTracking.monthlyUsedHours[monthIndex]++;
      this.monthlyHeaterTracking.monthlyUsedCapacity[monthIndex] += heaterUsedThisHour;
    } else {
      // Heater was available but not used - available for balance market
      this.monthlyHeaterTracking.monthlyAvailableHours[monthIndex]++;
    }
    
    // Update remaining deficit
    let remainingDeficit = demandResult.unmetDemand;
    
    // Process losses for all actions that produce An-værk output
    // (DISCHARGE_LTES_TO_DH and CONTROLLED_EMERGENCY_PURCHASE)
    demandResult.actions.forEach(action => {
      if (action.anPlant && action.abPlant) {
        const prodLoss = action.anPlant * CONSTANTS.EFFICIENCY.production_loss;
        const gridLoss = action.abPlant * CONSTANTS.EFFICIENCY.grid_loss;

        this.results.productionLosses += prodLoss;
        this.results.gridLosses += gridLoss;
        hourData.losses.production += prodLoss;
        hourData.losses.grid += gridLoss;
      }
    });
    
    // ========================================
    // 3. SKIP DUPLICATE PROCESSING - Already handled by DemandManager
    // ========================================
    // Waste heat, solar, and grid purchases are already handled by DemandManager
    // which ensures 100% demand coverage with proper priority:
    // 1. Storage first
    // 2. Waste heat always (when LTES < 95%)
    // 3. Solar when available
    // 4. Grid as last resort
    
    // SIMPLIFIED APPROACH: Just track everything as it was, but calculate ACTUAL An-værk needs at the end
    // First pass: Track all energy inputs (with HEATER cap clipping per hour)
    let heaterCapUsed = 0;
    const heaterCap = this.constraints.heater_max_MW || 30;
    
    demandResult.actions.forEach(action => {
      const isHeaterCharge = (t) => (
        t === 'OP_CHARGE_LTES_SOLAR' || t === 'OP_CHARGE_LTES_GRID' ||
        t === 'OP_CHARGE_HTES_SOLAR' || t === 'OP_CHARGE_HTES_GRID'
      );
      // Apply heater capacity cap by clipping action.amount and proportional fields
      if (action && action.type && isHeaterCharge(action.type)) {
        const originalAmt = action.amount || 0;
        const allowed = Math.max(0, heaterCap - heaterCapUsed);
        const applied = Math.min(originalAmt, allowed);
        if (applied < originalAmt) {
          const ratio = originalAmt > 0 ? (applied / originalAmt) : 0;
          action.amount = applied;
          if (typeof action.stored === 'number') action.stored = action.stored * ratio;
          if (typeof action.cost === 'number') action.cost = action.cost * ratio;
        }
        heaterCapUsed += action.amount || 0;
      }
      // Waste heat (operational) - NOT counted as An-værk electricity input!
      if (action.type === 'CHARGE_LTES_WASTE_HEAT' || action.type === 'CHARGE_LTES_WASTE_HEAT_RECOVERED') {
        this.results.operationalFactoryWasteHeatUsed += action.amount;
        this.results.operationalFactoryWasteHeatCost += action.cost || 0;
        this.results.wasteHeatAccepted += action.amount;  // Track accepted waste heat
        totalCost += action.cost || 0;  // Add to totalCost for this hour
        // CRITICAL: DO NOT add to energyInputThisHour - waste heat goes directly to LTES, bypasses An-værk!
      }
      // DEPRECATED: Direkte LTES køb - skal nu gå via HTES
      // Heliac flow: Elnet/Sol → Kedel → HTES → Turbine → LTES → Demand
      else if (action.type === 'OP_CHARGE_LTES_SOLAR') {
        console.warn('DEPRECATED: OP_CHARGE_LTES_SOLAR - al sol skal gå via HTES. Behandler som HTES charge.');
        // Redirect til HTES tracking for at bevare data
        this.results.operationalSolarPurchased += action.amount;
        this.results.operationalSolarCost += action.cost || 0;
        this.results.solarPurchased += action.amount;
        totalCost += action.cost || 0;
        energyInputThisHour += action.amount;
        // Track som HTES charge (da det er det korrekte flow)
        this.results.htesOperationalCharged += action.amount;
        this.results.htesOperationalChargeCost += action.cost || 0;
        this.results.htesTotalCharged += action.amount;
        this.results.htesTotalChargeCost += action.cost || 0;
      }
      else if (action.type === 'OP_CHARGE_LTES_GRID') {
        console.warn('DEPRECATED: OP_CHARGE_LTES_GRID - al grid skal gå via HTES. Behandler som HTES charge.');
        // Redirect til HTES tracking for at bevare data
        this.results.operationalGridPurchased += action.amount;
        this.results.operationalGridCost += action.cost || 0;
        this.results.electricityPurchased += action.amount;
        totalCost += action.cost || 0;
        energyInputThisHour += action.amount;
        // Track som HTES charge (da det er det korrekte flow)
        this.results.htesOperationalCharged += action.amount;
        this.results.htesOperationalChargeCost += action.cost || 0;
        this.results.htesTotalCharged += action.amount;
        this.results.htesTotalChargeCost += action.cost || 0;
      }
      // HTES charging is for storage/turbine operations
      else if (action.type === 'OP_CHARGE_HTES_SOLAR') {
        this.results.operationalSolarPurchased += action.amount;
        this.results.operationalSolarCost += action.cost || 0;
        this.results.solarPurchased += action.amount;
        totalCost += action.cost || 0;
        energyInputThisHour += action.amount;

        // Track HTES operational charge
        this.results.htesOperationalCharged += action.amount;
        this.results.htesOperationalChargeCost += action.cost || 0;
        this.results.htesTotalCharged += action.amount;
        this.results.htesTotalChargeCost += action.cost || 0;
      }
      // HTES charging is for storage/turbine operations
      else if (action.type === 'OP_CHARGE_HTES_GRID') {
        this.results.operationalGridPurchased += action.amount;
        this.results.operationalGridCost += action.cost || 0;
        this.results.electricityPurchased += action.amount;
        totalCost += action.cost || 0;
        energyInputThisHour += action.amount;

        // Track HTES operational charge
        this.results.htesOperationalCharged += action.amount;
        this.results.htesOperationalChargeCost += action.cost || 0;
        this.results.htesTotalCharged += action.amount;
        this.results.htesTotalChargeCost += action.cost || 0;
      }
      // Proaktivt HTES køb ved lave priser - smart strategi
      else if (action.type === 'PROACTIVE_HTES_CHARGE') {
        this.results.operationalGridPurchased += action.amount;
        this.results.operationalGridCost += action.cost || 0;
        this.results.electricityPurchased += action.amount;
        totalCost += action.cost || 0;
        energyInputThisHour += action.amount;

        // Track HTES operational charge - proaktivt køb er operational
        this.results.htesOperationalCharged += action.amount;
        this.results.htesOperationalChargeCost += action.cost || 0;
        this.results.htesTotalCharged += action.amount;
        this.results.htesTotalChargeCost += action.cost || 0;
      }
      // Legacy arbitrage HTES actions - these should no longer be generated
      // ARBITRAGE = ONLY battery (3MW capacity, 366 cycles/year)
      else if (action.type === 'ARB_CHARGE_HTES_SOLAR') {
        console.warn('DEPRECATED: ARB_CHARGE_HTES_SOLAR should not be used. Use OP_CHARGE_HTES_SOLAR instead.');
        // Treat as operational to prevent data loss
        this.results.operationalSolarPurchased += action.amount;
        this.results.operationalSolarCost += action.cost || 0;
        this.results.solarPurchased += action.amount;  // Track total solar for UI
        totalCost += action.cost || 0;  // Add to operational cost
        energyInputThisHour += action.amount;
      }
      else if (action.type === 'ARB_CHARGE_HTES_GRID') {
        console.warn('DEPRECATED: ARB_CHARGE_HTES_GRID should not be used. Use OP_CHARGE_HTES_GRID instead.');
        // Treat as operational to prevent data loss
        this.results.operationalGridPurchased += action.amount;
        this.results.operationalGridCost += action.cost || 0;
        this.results.electricityPurchased += action.amount;  // Track total electricity for UI
        totalCost += action.cost || 0;  // Add to operational cost
        energyInputThisHour += action.amount;
      }
      // Turbine el-salg - ALL turbine operations are OPERATIONAL
      else if (action.type === 'DISCHARGE_HTES_TURBINE') {
        const turbineElectricity = action.electricity || 0;
        const turbineHeat = action.heat || 0;
        const htesInput = action.input || action.htesInput || 0;  // Fix: DemandManager uses 'input', not 'htesInput'

        this.results.electricityProduced += turbineElectricity;
        this.results.turbineElectricityProduced += turbineElectricity;
        this.results.electricitySold += turbineElectricity;
        this.results.electricityRevenue += action.revenue || 0;
        this.results.turbineRevenue += action.revenue || 0;

        // Track HTES total discharge (operational)
        this.results.htesOperationalDischarged += htesInput;
        this.results.htesOperationalElProduced += turbineElectricity;
        this.results.htesOperationalHeatProduced += turbineHeat;
        this.results.htesTotalDischarged += htesInput;
        this.results.htesTotalElProduced += turbineElectricity;
        this.results.htesTotalHeatProduced += turbineHeat;
        this.results.htesTotalElRevenue += (action.revenue || 0);

        // Track effective full-load hours (EFLH) at 10 MW turbine
        const fullLoadHours = (turbineElectricity / (this.constraints.turbine_max_MW || 10));
        this.results.turbinePartialHours += fullLoadHours;
        // Operating hours counted by activations below (kept for consistency)
        this.results.turbineOperatingHours++;
      }
      // Turbine for waste heat space - OPERATIONAL
      else if (action.type === 'DISCHARGE_HTES_TURBINE_FOR_WASTE') {
        const turbineElectricity = action.electricity || 0;
        const turbineHeat = action.heat || 0;
        const htesInput = action.input || 0;
        this.results.electricityProduced += turbineElectricity;
        this.results.turbineElectricityProduced += turbineElectricity;
        this.results.electricitySold += turbineElectricity;
        this.results.electricityRevenue += action.revenue || 0;
        this.results.turbineRevenue += action.revenue || 0;
        // KRITISK: Track HTES totals for consistency
        this.results.htesOperationalDischarged += htesInput;
        this.results.htesOperationalElProduced += turbineElectricity;
        this.results.htesOperationalHeatProduced += turbineHeat;
        this.results.htesTotalDischarged += htesInput;
        this.results.htesTotalElProduced += turbineElectricity;
        this.results.htesTotalHeatProduced += turbineHeat;
        const fullLoadHours2 = (turbineElectricity / (this.constraints.turbine_max_MW || 10));
        this.results.turbinePartialHours += fullLoadHours2;
        this.results.turbineOperatingHours++;
      }
      // Turbine for demand - OPERATIONAL
      else if (action.type === 'DISCHARGE_HTES_TO_LTES_FOR_DEMAND' || action.type === 'DISCHARGE_HTES_ECONOMICAL_WASTE' || action.type === 'LAST_RESORT_TURBINE') {
        const turbineElectricity = action.electricity || 0;
        const turbineHeat = action.heat || 0;
        const htesInput = action.input || 0;
        this.results.electricityProduced += turbineElectricity;
        this.results.turbineElectricityProduced += turbineElectricity;
        this.results.electricitySold += turbineElectricity;
        this.results.electricityRevenue += action.revenue || 0;
        this.results.turbineRevenue += action.revenue || 0;
        // KRITISK: Track HTES totals for consistency
        this.results.htesOperationalDischarged += htesInput;
        this.results.htesOperationalElProduced += turbineElectricity;
        this.results.htesOperationalHeatProduced += turbineHeat;
        this.results.htesTotalDischarged += htesInput;
        this.results.htesTotalElProduced += turbineElectricity;
        this.results.htesTotalHeatProduced += turbineHeat;
        const fullLoadHours3 = (turbineElectricity / (this.constraints.turbine_max_MW || 10));
        this.results.turbinePartialHours += fullLoadHours3;
        this.results.turbineOperatingHours++;
      }
      // Emergency HTES discharge til demand (korrekt Heliac flow)
      else if (action.type === 'EMERGENCY_HTES_TO_DEMAND') {
        const turbineElectricity = action.electricity || 0;
        const turbineHeat = action.heat || 0;
        const htesInput = action.input || 0;
        this.results.electricityProduced += turbineElectricity;
        this.results.turbineElectricityProduced += turbineElectricity;
        this.results.electricitySold += turbineElectricity;
        this.results.electricityRevenue += action.revenue || 0;
        this.results.turbineRevenue += action.revenue || 0;
        // Track HTES totals
        this.results.htesOperationalDischarged += htesInput;
        this.results.htesOperationalElProduced += turbineElectricity;
        this.results.htesOperationalHeatProduced += turbineHeat;
        this.results.htesTotalDischarged += htesInput;
        this.results.htesTotalElProduced += turbineElectricity;
        this.results.htesTotalHeatProduced += turbineHeat;
        this.results.htesTotalElRevenue += action.revenue || 0;
        const fullLoadHours4 = (turbineElectricity / (this.constraints.turbine_max_MW || 10));
        this.results.turbinePartialHours += fullLoadHours4;
        this.results.turbineOperatingHours++;
      }
      // Emergency grid køb via HTES→Turbine→Demand (korrekt Heliac flow)
      else if (action.type === 'EMERGENCY_GRID_TO_HTES_TO_DEMAND') {
        // Track el køb
        this.results.operationalGridPurchased += action.purchasedMWh || 0;
        this.results.operationalGridCost += action.cost || 0;
        this.results.electricityPurchased += action.purchasedMWh || 0;
        totalCost += action.cost || 0;
        energyInputThisHour += action.purchasedMWh || 0;

        // Track HTES charge
        this.results.htesOperationalCharged += action.htesStored || 0;
        this.results.htesOperationalChargeCost += action.cost || 0;
        this.results.htesTotalCharged += action.htesStored || 0;
        this.results.htesTotalChargeCost += action.cost || 0;

        // Track turbine output
        const turbineElectricity = action.electricity || 0;
        const turbineHeat = action.heat || 0;
        const htesInput = action.htesInput || action.htesStored || 0;
        this.results.electricityProduced += turbineElectricity;
        this.results.turbineElectricityProduced += turbineElectricity;
        this.results.electricitySold += turbineElectricity;
        this.results.electricityRevenue += action.revenue || 0;
        this.results.turbineRevenue += action.revenue || 0;

        // Track HTES discharge
        this.results.htesOperationalDischarged += htesInput;
        this.results.htesOperationalElProduced += turbineElectricity;
        this.results.htesOperationalHeatProduced += turbineHeat;
        this.results.htesTotalDischarged += htesInput;
        this.results.htesTotalElProduced += turbineElectricity;
        this.results.htesTotalHeatProduced += turbineHeat;
        this.results.htesTotalElRevenue += action.revenue || 0;
        const fullLoadHours5 = (turbineElectricity / (this.constraints.turbine_max_MW || 10));
        this.results.turbinePartialHours += fullLoadHours5;
        this.results.turbineOperatingHours++;
      }
      // Negative priser - CHARGE_LTES_NEGATIVE only
      // NOTE: CHARGE_HTES_NEGATIVE trackes direkte hvor actions oprettes (linje 341-350)
      // fordi de er i hourData.actions, ikke demandResult.actions
      else if (action.type === 'CHARGE_LTES_NEGATIVE') {
        // Negative prices - we get PAID to buy!
        totalRevenue += action.revenue || 0;
        // Operational - tæller med i energy input
        energyInputThisHour += action.amount;
        this.results.operationalGridPurchased += action.amount;
        this.results.electricityPurchased += action.amount;
      }
      // Turbine arbitrage ved negative priser
      else if (action.type === 'TURBINE_NEGATIVE_PRICE_ARBITRAGE') {
        const turbineElectricity = action.electricityOut || 0;
        const htesInput = action.htesInput || 0;
        const turbineHeat = action.heatOut || 0;
        totalRevenue += action.revenue || 0;
        this.results.electricityProduced += turbineElectricity;
        this.results.turbineElectricityProduced += turbineElectricity;
        this.results.electricitySold += turbineElectricity;
        this.results.electricityRevenue += action.revenue || 0;
        this.results.turbineRevenue += action.revenue || 0;
        // KRITISK: Track HTES totals for consistency med andre turbine actions
        this.results.htesTotalDischarged += htesInput;
        this.results.htesTotalElProduced += turbineElectricity;
        this.results.htesTotalHeatProduced += turbineHeat;
        this.results.htesTotalElRevenue += (action.revenue || 0);
        const fullLoadHours = (turbineElectricity / (this.constraints.turbine_max_MW || 20));
        this.results.turbinePartialHours += fullLoadHours;
        this.results.turbineOperatingHours++;  // Track operating hours (nu her i stedet for i loop)
      }
    });

    // ========================================
    // 3B. HTES EL-ARBITRAGE (EFTER DEMAND ER DÆKKET)
    // ========================================
    // Kør turbine for el-produktion hvis:
    // 1. HTES har overskud (>30% niveau)
    // 2. El-prisen er høj (>350 DKK)
    // 3. LTES har plads til varmen fra turbinen
    const htesLevelAfterDemand = this.storage.getLevels().htes;
    const htesAvailableAfterDemand = htesLevelAfterDemand * this.storage.storage.htes.capacity;
    const ltesSpaceAfterDemand = this.storage.getAvailableSpace().ltes;

    // PREMIUM PEAK-PRICE TURBINE STRATEGI
    // Mål: SÆLG KUN VED HØJE PRISER for at maksimere el-salg profit
    // Nøgle: Bedre at sælge mindre ved høje priser end mere ved lave priser!

    // HØJE minimums-priser for el-salg (vi vil have høj gns. salgspris!)
    const minSellPrice = 450; // ALDRIG sælg under 450 DKK/MWh

    if (htesAvailableAfterDemand > 10 && spotPrice > minSellPrice && ltesSpaceAfterDemand > 1) {
      // Check forecast: Find dagens ABSOLUT DYRESTE timer
      let isPremiumPeakHour = false;

      if (this.dataProvider) {
        const dayOfYear = Math.floor(this.currentHour / 24);
        const startHour = dayOfYear * 24;
        const hourOfDay = this.currentHour % 24;
        const forecastPrices = [];

        for (let h = 0; h < 24 && startHour + h < 8784; h++) {
          const hourIndex = startHour + h;
          let price = 0;
          if (typeof this.dataProvider === 'function') {
            const data = this.dataProvider(hourIndex);
            price = data ? data.spotPrice : 0;
          } else if (this.dataProvider && typeof this.dataProvider === 'object') {
            price = this.dataProvider.getSpotPrice ? this.dataProvider.getSpotPrice(hourIndex) : 0;
          }
          forecastPrices.push({ hour: h, price: price || 0 });
        }

        // Sorter efter pris (højeste først)
        const sortedByPrice = [...forecastPrices].sort((a, b) => b.price - a.price);

        // Find vores position i ranglisten
        const ourRank = sortedByPrice.findIndex(p => p.hour === hourOfDay) + 1;

        // SUPER SELEKTIV: KUN top 10% timer (ca. 2-3 timer per dag)
        // Vi vil have HØJE priser, ikke volumen!
        const premiumThreshold = Math.ceil(24 * 0.10); // Top 10% = ~2.4 timer
        isPremiumPeakHour = ourRank <= premiumThreshold;

        // PREMIUM BONUS: Hvis pris > 600 DKK - kør altid!
        if (spotPrice > 600) {
          isPremiumPeakHour = true;
        }
      } else {
        isPremiumPeakHour = spotPrice > 500; // Fallback
      }

      if (isPremiumPeakHour) {
        // Beregn profit BEFORE running turbine
        const avgHTESPrice = this.htesArbitrageTracking.totalChargeMWh > 0
          ? this.htesArbitrageTracking.totalChargeCost / this.htesArbitrageTracking.totalChargeMWh
          : 200; // Default

        // Kør turbine for el-arbitrage
        const turbineMaxMW = this.constraints.turbine_max_MW || 20;
        const turbineInput = Math.min(
          turbineMaxMW,
          htesAvailableAfterDemand - 5, // Behold 5 MWh reserve
          ltesSpaceAfterDemand / 0.85 // Max baseret på LTES plads
        );

        if (turbineInput > 1) {
          const discharged = this.storage.dischargeHTES(turbineInput);
          this.thermalPhysics.updateHTESDischarge(discharged, this.currentHour);

          // Turbine output
          const turbineEfficiency = this.thermalPhysics
            ? this.thermalPhysics.getTurbineEfficiency()
            : { heat: 0.85, electricity: 0.15 };

          const electricityOut = discharged * Math.max(turbineEfficiency.electricity, 0.15);
          const heatOut = discharged * turbineEfficiency.heat;

          // Gem varme i LTES
          const heatStored = this.storage.chargeLTES(Math.min(heatOut, ltesSpaceAfterDemand));
          this.thermalPhysics.updateLTESCharge(heatStored, this.currentHour);

          // KRITISK: Varme til LTES tæller IKKE som An-værk produktion her!
          // An-værk produktion tælles først når varmen BRUGES fra LTES til demand
          // (via DemandManager.deliverFromStorage → DISCHARGE_LTES_TO_DH)
          // anPlantProductionThisHour += heatStored;  // FJERNET - undgå dobbelt-tælling
          // energyOutputThisHour += heatStored;  // FJERNET

          const elRevenue = electricityOut * spotPrice;
          const heatValue = 200; // DKK/MWh
          const heatSavings = heatStored * heatValue;

          totalRevenue += elRevenue;

          // Track HTES arbitrage
          this.htesArbitrageTracking.totalDischargeMWh += electricityOut;
          this.htesArbitrageTracking.totalDischargeRevenue += elRevenue;
          this.htesArbitrageTracking.totalHeatProduced += heatStored;
          this.htesArbitrageTracking.totalHeatSavings += heatSavings;

          // Cycles
          if (this.htesArbitrageTracking.currentCycleCharge > 0) {
            this.htesArbitrageTracking.cycleCount++;
            this.htesArbitrageTracking.currentCycleCharge = 0;
          }

          // Track results
          this.results.htesArbitrageElectricityProduced += electricityOut;
          this.results.htesArbitrageRevenue += elRevenue;
          this.results.htesArbitrageHeatProduced += heatStored;
          this.results.htesArbitrageHeatSavings += heatSavings;

          // Track HTES totals
          // VIGTIGT: Brug heatOut (fuld turbine varme), ikke heatStored
          // Hvis LTES er fuld, "tabes" noget varme, men turbinen producerede den stadig
          this.results.htesTotalDischarged += discharged;
          this.results.htesTotalElProduced += electricityOut;
          this.results.htesTotalHeatProduced += heatOut;  // FIX: Brug heatOut, ikke heatStored
          this.results.htesTotalElRevenue += elRevenue;

          // Track turbine totals
          this.results.turbineElectricityProduced += electricityOut;
          this.results.turbineRevenue += elRevenue;
          this.results.turbineOperatingHours++;
          this.results.electricityProduced += electricityOut;
          this.results.electricitySold += electricityOut;
          this.results.electricityRevenue += elRevenue;

          const turbinePartialHours = discharged / (this.constraints.turbine_max_MW || 20);
          this.results.turbinePartialHours += turbinePartialHours;

          // Profit
          const inputCost = discharged * avgHTESPrice;
          const totalValue = elRevenue + heatSavings;
          const profit = totalValue - inputCost;

          hourData.actions.push({
            type: 'HTES_EL_ARBITRAGE',
            htesInput: discharged,
            electricityOut: electricityOut,
            heatOut: heatOut,
            heatStored: heatStored,
            elRevenue: elRevenue,
            heatSavings: heatSavings,
            spotPrice: spotPrice,
            avgHTESPrice: avgHTESPrice,
            profit: profit,
            reason: `HTES El-Arbitrage: ${electricityOut.toFixed(1)} MWh el @ ${spotPrice.toFixed(0)} DKK + ${heatStored.toFixed(1)} MWh varme (profit: ${profit.toFixed(0)} DKK)`
          });
        }
      }
    }

    // ========================================
    // 4. ARBITRAGE STRATEGIER (kun med overskydende sol)
    // ========================================
    // Find hvor meget sol der blev brugt i DemandManager (både OP_ og ARB_)
    const solarUsedInDemandManager = demandResult.actions
      .filter(a => typeof a.type === 'string' && a.type.includes('SOLAR'))
      .reduce((sum, a) => sum + (a.amount || 0), 0);
    const remainingSolar = Math.max(0, solarMWh - solarUsedInDemandManager);
    
    const arbitrageActions = this.priority.executeArbitrage(hour, spotPrice, remainingSolar);
    
    arbitrageActions.forEach(action => {
      if (action.type.includes('SOLAR')) {
        this.results.arbitrageSolarPurchased += action.amount;
        this.results.arbitrageSolarCost += action.cost;
        this.results.solarPurchased += action.amount;  // Track total solar for UI
        // IKKE totalCost - arbitrage skal ikke indgå i raw cost!
        // IKKE energyInputThisHour - arbitrage er ikke del af varme-input!
      } else if (action.type.includes('GRID')) {
        this.results.arbitrageGridPurchased += action.amount;
        this.results.arbitrageGridCost += action.cost;
        this.results.electricityPurchased += action.amount;  // Track total electricity for UI
        // IKKE totalCost - arbitrage skal ikke indgå i raw cost!
        // IKKE energyInputThisHour - arbitrage er ikke del af varme-input!
      } else if (action.type.includes('DISCHARGE')) {
        this.results.batteryArbitrageRevenue += action.revenue || 0;
        totalRevenue += action.revenue || 0;
      }
      
      hourData.actions.push(action);
    });
    
    // Track unmet demand
    if (remainingDeficit > 0.01) {
      // Unmet demand is not tracked anymore - handled via emergency actions
    }
    
    // Update solar tracking
    if (solarMWh > 0) {
      const solarUsed = hourData.actions
        .filter(a => a.type.includes('SOLAR'))
        .reduce((sum, a) => sum + a.amount, 0);
      
      const utilizationRatio = solarUsed / solarMWh;
      if (utilizationRatio >= 0.95) {
        this.results.fullPurchaseHours++;
      } else if (utilizationRatio > 0) {
        this.results.partialPurchaseHours++;
      }
    }
    
    // Battery arbitrage - OPTIMIZED for up to 3 cycles per day
    // Target: 3 cycles/day on high spread days, 2 on medium, 1 on low
    const dayOfYear = Math.floor(this.currentHour / 24);
    const hourOfDay = this.currentHour % 24;

    // Leap year 2024 has exactly 366 days (0-365)
    const isValidDay = dayOfYear >= 0 && dayOfYear < 366;
    if (!isValidDay) return; // Skip if beyond day 365

    // INCREASED LIMIT: Allow up to 3 cycles per day based on battery capacity
    const batteryCapacity = this.constraints.battery_capacity_MWh || 3;
    const MAX_ANNUAL_BATTERY_MWH = 366 * 3 * batteryCapacity;  // 366 days × 3 cycles × capacity
    const batteryThroughputSoFar = this.batteryDailyTracking.totalChargedMWh || 0;
    const remainingAllowance = Math.max(0, MAX_ANNUAL_BATTERY_MWH - batteryThroughputSoFar);
    
    // Skip battery operations if we've hit the annual limit
    if (remainingAllowance <= 0) {
      return hourData;  // Exit early - no more battery arbitrage allowed
    }
    
    // CRITICAL: Handle very first day (day 0) - ensure we start cycling immediately
    const isFirstDay = dayOfYear === 0;
    const isLastDay = dayOfYear === 365;
    
    // Find dagens billigste og dyreste timer - caching per dag
    if (!this.dailyPriceCache || this.dailyPriceCache.day !== dayOfYear) {
      this.dailyPriceCache = { day: dayOfYear, cheapest: null, mostExpensive: null };
      
      if (this.dataProvider) {
        const todayPrices = [];
        const startHour = dayOfYear * 24;
        const hoursInDay = 24; // All days have 24 hours in 2024

        for (let h = 0; h < hoursInDay && startHour + h < 8784; h++) {
          const hourIndex = startHour + h;
          // Handle both function and object data providers
          let spotPrice;
          if (typeof this.dataProvider === 'function') {
            const data = this.dataProvider(hourIndex);
            spotPrice = data ? data.spotPrice : 0;
          } else if (this.dataProvider && typeof this.dataProvider === 'object') {
            spotPrice = this.dataProvider.getSpotPrice ? this.dataProvider.getSpotPrice(hourIndex) : 0;
          }

          // Always add all 24 hours, even with 0 price
          todayPrices.push({
            hourOfDay: h,
            absoluteHour: hourIndex,
            price: spotPrice || 0
          });
        }
        
        if (todayPrices.length > 0) {
          // Store todayPrices for BatteryOptimizer
          this.dailyPriceCache.todayPrices = todayPrices;

          // Find 3 billigste og 3 dyreste timer baseret på spot priser
          // Note: Vi kan ikke vide sol tilgængelighed på forhånd, så vi bruger spot + TSO for planlægning
          // Faktisk sol prioritering sker når vi når den time

          // Find billigste timer til charge (med TSO da vi ikke ved om sol er tilgængelig)
          const sortedByPrice = [...todayPrices].sort((a, b) => a.price - b.price);
          const cheapestHours = sortedByPrice.slice(0, 3).map(p => p.hourOfDay);

          // Find dyreste timer til discharge
          const sortedByPriceDesc = [...todayPrices].sort((a, b) => b.price - a.price);
          const expensiveHours = sortedByPriceDesc.slice(0, 3).map(p => p.hourOfDay);

          this.dailyPriceCache.cheapest = cheapestHours[0]; // Primary cheap hour
          this.dailyPriceCache.cheapestHours = cheapestHours; // All cheap hours
          this.dailyPriceCache.mostExpensive = expensiveHours[0]; // Primary expensive hour
          this.dailyPriceCache.expensiveHours = expensiveHours; // All expensive hours
        } else {
          // Fallback for last day or missing data
          this.dailyPriceCache.cheapest = isLastDay ? 1 : 2;
          this.dailyPriceCache.cheapestHours = [isLastDay ? 1 : 2];
          this.dailyPriceCache.mostExpensive = isLastDay ? 5 : 17;
          this.dailyPriceCache.expensiveHours = [isLastDay ? 5 : 17];
        }
      }
    }
    
    // Track multiple cycles per day instead of boolean
    if (!this.batteryDailyTracking.dailyCycleCount) {
      this.batteryDailyTracking.dailyCycleCount = new Array(366).fill(0);
    }
    const cyclesToday = this.batteryDailyTracking.dailyCycleCount[dayOfYear] || 0;

    // Get today's price data for BatteryOptimizer
    const todayPricesData = this.dailyPriceCache.todayPrices || [];

    // Get today's solar data
    const todaySolarData = [];
    if (this.dataProvider) {
      const startHour = dayOfYear * 24;
      for (let h = 0; h < 24 && startHour + h < 8784; h++) {
        const hourIndex = startHour + h;
        let solarMWh = 0;
        if (typeof this.dataProvider === 'function') {
          const data = this.dataProvider(hourIndex);
          solarMWh = data ? data.solarMWh : 0;
        } else if (this.dataProvider && typeof this.dataProvider === 'object') {
          solarMWh = this.dataProvider.getSolarMWh ? this.dataProvider.getSolarMWh(hourIndex) : 0;
        }
        todaySolarData[h] = solarMWh || 0;
      }
    }

    // NEW MULTI-CYCLE BATTERY STRATEGY using BatteryOptimizer
    const batteryLevel = this.storage.storage.battery.current;

    // Get cycle status for today (up to 4 cycles)
    const hasChargedCycle1 = this.batteryDailyTracking.cycle1Charged[dayOfYear];
    const hasDischargedCycle1 = this.batteryDailyTracking.cycle1Discharged[dayOfYear];
    const hasChargedCycle2 = this.batteryDailyTracking.cycle2Charged[dayOfYear];
    const hasDischargedCycle2 = this.batteryDailyTracking.cycle2Discharged[dayOfYear];
    const hasChargedCycle3 = this.batteryDailyTracking.cycle3Charged[dayOfYear];
    const hasDischargedCycle3 = this.batteryDailyTracking.cycle3Discharged[dayOfYear];
    const hasChargedCycle4 = this.batteryDailyTracking.cycle4Charged[dayOfYear];
    const hasDischargedCycle4 = this.batteryDailyTracking.cycle4Discharged[dayOfYear];

    // Get decision from optimizer
    const batteryDecision = this.batteryOptimizer.shouldChargeOrDischarge(
      hourOfDay,
      todayPricesData,
      batteryLevel,
      hasChargedCycle1,
      hasDischargedCycle1,
      hasChargedCycle2,
      hasDischargedCycle2,
      hasChargedCycle3,
      hasDischargedCycle3,
      hasChargedCycle4,
      hasDischargedCycle4,
      todaySolarData
    );
    
    // Execute battery decision
    if (batteryDecision.action === 'charge') {
      const space = this.storage.getAvailableSpace();
      // Target up to battery capacity but respect available space and charge rate
      const maxCharge = Math.min(
        this.constraints.battery_capacity_MWh,
        this.constraints.battery_max_charge_MW
      );
      const targetCharge = Math.min(maxCharge, space.battery);
      if (targetCharge > 0.05) {
        // Use solar first, then grid top-up. Always pick cheapest mix.
        const availableSolar = Math.max(0, apiData.solarMWh || 0);
        const solarUsed = Math.min(availableSolar, targetCharge);
        const gridUsed = Math.max(0, targetCharge - solarUsed);

        // Execute battery charge for total target
        const result = this.storage.chargeBattery(targetCharge);
        const unitSolarPrice = spotPrice; // solar has no TSO; allow negative prices
        const unitGridPrice = spotPrice + CONSTANTS.TSO_AFGIFT; // grid adds TSO; may still be negative
        const cost = (solarUsed * unitSolarPrice) + (gridUsed * unitGridPrice);

        // Track purchases and costs
        if (solarUsed > 0) {
          this.results.arbitrageSolarPurchased += solarUsed;
          this.results.arbitrageSolarCost += solarUsed * unitSolarPrice;
          this.results.solarPurchased += solarUsed;
        }
        if (gridUsed > 0) {
          this.results.arbitrageGridPurchased += gridUsed;
          this.results.arbitrageGridCost += gridUsed * unitGridPrice;
          this.results.electricityPurchased += gridUsed;
        }
        this.results.batteryChargeAmount += targetCharge;
        this.results.batteryChargeCost += cost;  // SEPARAT tracking af batteri cost

        // Track multi-cycle based on cycle number
        if (dayOfYear < 366) {
          if (batteryDecision.cycle === 1) this.batteryDailyTracking.cycle1Charged[dayOfYear] = true;
          else if (batteryDecision.cycle === 2) this.batteryDailyTracking.cycle2Charged[dayOfYear] = true;
          else if (batteryDecision.cycle === 3) this.batteryDailyTracking.cycle3Charged[dayOfYear] = true;
          else if (batteryDecision.cycle === 4) this.batteryDailyTracking.cycle4Charged[dayOfYear] = true;
          this.batteryDailyTracking.dailyCycleCount[dayOfYear] += 0.5;
          this.batteryDailyTracking.dailyChargeRecord[dayOfYear] = true;
        }
        this.batteryDailyTracking.totalChargeCycles++;
        this.batteryDailyTracking.totalChargedMWh += targetCharge;

        hourData.actions.push({
          type: 'ARB_BATTERY_CHARGE',
          amount: targetCharge,
          stored: result.stored,
          loss: result.loss,
          cost,
        category: 'ARBITRAGE',
          source: solarUsed > 0 && gridUsed > 0 ? 'MIX' : (solarUsed > 0 ? 'SOLAR' : 'GRID'),
          solarUsed,
          gridUsed,
          dayOfYear: dayOfYear,
          cycleNumber: this.batteryDailyTracking.totalChargeCycles,
          reason: `Charge ${targetCharge.toFixed(2)} MWh (sol ${solarUsed.toFixed(2)}, grid ${gridUsed.toFixed(2)}) at spot ${spotPrice.toFixed(0)} DKK/MWh`
        });
      }
    }
    
    if (batteryDecision.action === 'discharge' && this.storage.storage.battery.current > 0.1) {
      // Discharge ALL available battery at most expensive hour
      const discharge = Math.min(
        this.storage.storage.battery.current,  // Discharge everything
        this.constraints.battery_max_discharge_MW  // Configurable max discharge rate
      );
      if (discharge > 0) {
        const result = this.storage.dischargeBattery(discharge);
        const revenue = result.output * spotPrice;
        totalRevenue += revenue;
        this.results.batteryArbitrageRevenue += revenue;
        this.results.arbitrageElectricitySold += result.output;
        this.results.arbitrageElectricityRevenue += revenue;
        this.results.batteryDischargeAmount += result.output;
        this.results.batteryLosses += result.loss;
        // Track battery electricity separately
        this.results.batteryElectricitySold += result.output;
        this.results.batteryRevenue += revenue;
        
        // Track multi-cycle based on which cycle we're in
        if (dayOfYear < 366) {
          if (batteryDecision.cycle === 1) {
            this.batteryDailyTracking.cycle1Discharged[dayOfYear] = true;
          } else if (batteryDecision.cycle === 2) {
            this.batteryDailyTracking.cycle2Discharged[dayOfYear] = true;
          } else if (batteryDecision.cycle === 3) {
            this.batteryDailyTracking.cycle3Discharged[dayOfYear] = true;
          } else if (batteryDecision.cycle === 4) {
            this.batteryDailyTracking.cycle4Discharged[dayOfYear] = true;
          }
          this.batteryDailyTracking.dailyCycleCount[dayOfYear] += 0.5;
          this.batteryDailyTracking.dailyDischargeRecord[dayOfYear] = true;
        }
        this.batteryDailyTracking.totalDischargeCycles++;
        this.batteryDailyTracking.totalDischargedMWh += result.output;
        
        hourData.actions.push({
          type: 'ARB_BATTERY_DISCHARGE',
          amount: discharge,
          output: result.output,
          loss: result.loss,
          category: 'ARBITRAGE',
          revenue,
          dayOfYear: dayOfYear,
          cycleNumber: this.batteryDailyTracking.totalDischargeCycles,
          reason: `Daily discharge #${this.batteryDailyTracking.totalDischargeCycles} at ${spotPrice.toFixed(0)} DKK/MWh`
        });
      }
    }
    
    // DISABLED: Force charging logic - let optimizer handle everything
    const skipForcedCharging = true;
    const lastChanceHour = isLastDay ? 5 : 22; // Earlier on last partial day
    const absoluteFinalHour = isLastDay ? 6 : 23;

    if (hourOfDay >= lastChanceHour && !skipForcedCharging) {
      // Force charge if we haven't had a cycle today
      if (cyclesToday < 1 && dayOfYear < 366 && remainingAllowance > 0) {
        // Hvis batteriet er over 50% fuldt, aflad først for at gøre plads
        if (this.storage.storage.battery.current > 1.5) {
          const preDischarge = this.storage.storage.battery.current;
          const result = this.storage.dischargeBattery(preDischarge);
          // Denne forafladning tæller IKKE som dagens discharge-cykel
          hourData.actions.push({
            type: 'ARB_BATTERY_PRE_DISCHARGE',
            amount: preDischarge,
            output: result.output,
            category: 'ARBITRAGE',
            reason: 'Pre-discharge to make room for forced daily charge'
          });
        }
        
        // Nu lad op til fuld kapacitet (3 MWh) - BUT respect annual limit
        const forceCharge = Math.min(
          this.constraints.battery_max_charge_MW,
          this.constraints.battery_capacity_MWh,
          remainingAllowance  // Respect annual limit
        );
        const result = this.storage.chargeBattery(forceCharge);
        const cost = forceCharge * (Math.max(0, spotPrice) + CONSTANTS.TSO_AFGIFT);
        this.results.arbitrageGridPurchased += forceCharge;
        this.results.arbitrageGridCost += cost;
        this.results.electricityPurchased += forceCharge;  // Track total electricity for UI
        this.results.batteryChargeAmount += forceCharge;
        this.results.batteryChargeCost += cost;  // SEPARAT tracking af batteri cost
        this.batteryDailyTracking.lastChargeDay = dayOfYear;
        if (dayOfYear < 366) this.batteryDailyTracking.dailyChargeRecord[dayOfYear] = true;
        this.batteryDailyTracking.totalChargeCycles++;
        
        hourData.actions.push({
          type: 'ARB_BATTERY_CHARGE_FORCED',
          amount: forceCharge,
          stored: result.stored,
          category: 'ARBITRAGE',
          cost,
          reason: `FORCED: Day ${dayOfYear+1} mandatory charge at hour ${hourOfDay}`
        });
      }
      
      // Force discharge if we have charged but not discharged proportionally
      const chargeCount = Math.floor(cyclesToday + 0.5);
      const dischargeCount = Math.floor(cyclesToday);
      if (chargeCount > dischargeCount && hourOfDay >= absoluteFinalHour && dayOfYear < 366) {
        // Hvis batteriet er tomt, lad det først op
        if (this.storage.storage.battery.current < 0.5) {
          const emergencyCharge = Math.min(
            this.constraints.battery_max_charge_MW,
            2.0 // Lad mindst 2 MWh op
          );
          this.storage.chargeBattery(emergencyCharge);
          // Denne nødladning tæller IKKE som dagens charge-cykel
          hourData.actions.push({
            type: 'ARB_BATTERY_EMERGENCY_CHARGE',
            amount: emergencyCharge,
            reason: 'Emergency charge to enable forced daily discharge'
          });
        }
        
        // Nu aflad mindst 1 MWh for at registrere en cykel
        const forceDischarge = Math.max(
          1.0, // Mindst 1 MWh
          Math.min(
            this.constraints.battery_max_discharge_MW,
            this.storage.storage.battery.current
          )
        );
        const result = this.storage.dischargeBattery(forceDischarge);
        const revenue = result.output * spotPrice;
        totalRevenue += revenue;
        this.results.batteryArbitrageRevenue += revenue;
        this.results.arbitrageElectricitySold += result.output;
        this.batteryDailyTracking.lastDischargeDay = dayOfYear;
        if (dayOfYear < 366) this.batteryDailyTracking.dailyDischargeRecord[dayOfYear] = true;
        this.batteryDailyTracking.totalDischargeCycles++;
        
        hourData.actions.push({
          type: 'ARB_BATTERY_DISCHARGE_FORCED',
          amount: forceDischarge,
          output: result.output,
          category: 'ARBITRAGE',
          revenue,
          reason: `FORCED: Day ${dayOfYear+1} mandatory discharge at hour ${hourOfDay}`
        });
      }
    }

    // Battery system services revenue when idle and SOC >= min
    const batterySoc = this.storage.getLevels().battery; // fraction 0..1
    const isBatteryIdle = !(hourData.actions.some(a => a.type === 'ARB_BATTERY_CHARGE' || a.type === 'ARB_BATTERY_DISCHARGE'));
    if (isBatteryIdle && batterySoc >= (CONSTANTS.BATTERY_SERVICES.minSocFraction || 0.1)) {
      const rate = (CONSTANTS.BATTERY_SERVICES.ratePerMWPerHour || 0) * (this.constraints.battery_max_discharge_MW || 3);
      if (rate > 0) {
        this.results.batteryServiceRevenue += rate;
        this.results.batteryRevenue += 0; // keep separate
        this.results.batteryServiceHours += 1;
        totalRevenue += 0; // service goes to separate line; not counted as electricity revenue
        hourData.actions.push({
          type: 'BATTERY_SERVICE_IDLE',
          revenue: rate,
          category: 'SERVICE',
          note: 'Capacity revenue when battery idle',
          soc: batterySoc
        });
      }
    }

    // Turbine system services revenue when available (HTES temp/energy) and not actively producing
    const htesState = this.thermalPhysics.getState().htes;
    const turbineAvailable = htesState.canDischarge && (this.storage.storage.htes?.current || 0) > 0.5;
    const turbineProducing = hourData.actions.some(a => a.type === 'DISCHARGE_HTES_TURBINE');
    if (turbineAvailable && !turbineProducing) {
      const rate = (CONSTANTS.TURBINE_SERVICES.ratePerMWPerHour || 0) * (this.constraints.turbine_max_MW || 10);
      if (rate > 0) {
        this.results.turbineServiceRevenue += rate;
        this.results.turbineServiceHours += 1;
        hourData.actions.push({
          type: 'TURBINE_SERVICE_IDLE',
          revenue: rate,
          category: 'SERVICE',
          htesTemp: htesState.temperature,
          note: 'Capacity revenue when turbine available from HTES'
        });
      }
    }

    // Update totals
    this.results.anPlantProduction += anPlantProductionThisHour;
    // Heat delivery is tracked via anPlantProduction
    
    // Calculate Ab and Net
    let totalAbPlant = 0;
    let totalNetDelivery = 0;
    
    hourData.actions.forEach(action => {
      if (action.abPlant) totalAbPlant += action.abPlant;
      if (action.netDelivery) totalNetDelivery += action.netDelivery;
    });
    
    this.results.abPlantDelivery += totalAbPlant;
    this.results.netDelivery += totalNetDelivery;

    // Energy balance removed - use An/Ab/Net instead
    
    // OPEX costs
    const opexCost = anPlantProductionThisHour * CONSTANTS.OPEX_COST_PER_MWH;

    // Heater (elkedel) effective-hour tracking at 30 MW: sum energy into HEATER / 30 MW
    // We approximate heater-use this hour via actions that use heater (OP_ and NEGATIVE via grid/solar to LTES/HTES)
    const heaterEnergyThisHour = [
      ...demandResult.actions,
      ...hourData.actions
    ].filter(a => a && a.type && (
      a.type === 'OP_CHARGE_LTES_SOLAR' || a.type === 'OP_CHARGE_LTES_GRID' ||
      a.type === 'OP_CHARGE_HTES_SOLAR' || a.type === 'OP_CHARGE_HTES_GRID' ||
      a.type === 'CHARGE_LTES_NEGATIVE' || a.type === 'CHARGE_HTES_NEGATIVE'
    )).reduce((sum, a) => sum + (a.amount || 0), 0);

    if (!this.results.heaterEffectiveHours) this.results.heaterEffectiveHours = 0;
    const heaterMW = this.constraints.heater_max_MW || 30;
    this.results.heaterEffectiveHours += (heaterEnergyThisHour / heaterMW);
    this.results.totalOpexCost += opexCost;
    
    // Update totalOperationalCost (only operational, not arbitrage)
    this.results.totalOperationalCost += totalCost;
    
    // Update consumed inventory tracking
    this.results.consumedInitialInventoryValueDKK = this.storage.consumedInitialInventory.valueDKK;
    
    // Update hour data
    hourData.costs = {
      raw: totalCost,
      revenue: totalRevenue,
      net: totalCost - totalRevenue,
      opex: opexCost,
      cumulative_raw: this.results.totalOperationalCost,
      cumulative_total: this.results.totalOperationalCost + this.results.totalOpexCost
    };
    
    const levels = this.storage.getLevels();
    hourData.storage = {
      htes_level: levels.htes * 100,
      ltes_level: levels.ltes * 100,
      battery_level: levels.battery * 100
    };
    
    hourData.energyBalance = {
      input: energyInputThisHour,
      output: energyOutputThisHour,
      anProduction: anPlantProductionThisHour
    };
    
    hourData.energyPurchased = energyInputThisHour;
    
    // Add forecast info if available
    if (recommendation) {
      hourData.forecast = {
        priceContext: recommendation.priceContext,
        nextHighPrice: recommendation.nextHighPrice,
        nextLowPrice: recommendation.nextLowPrice,
        targetLTES: recommendation.targetLTESLevel,
        targetHTES: recommendation.targetHTESLevel
      };
    }
    
    // Check for thermal physics violations
    const violations = this.thermalPhysics.checkViolations(hourData.actions, this.currentHour);
    if (violations.length > 0) {
      // Add violations to hour data
      hourData.thermalViolations = violations;

      // Log critical violations
      violations.forEach(v => {
        if (v.severity === 'CRITICAL') {
          console.error(`❌ THERMAL VIOLATION at hour ${v.hour}: ${v.message}`);
        } else if (v.severity === 'WARNING') {
          console.warn(`⚠️ Thermal warning at hour ${v.hour}: ${v.message}`);
        }
      });
    }

    // Add thermal state to hour data
    hourData.thermalState = this.thermalPhysics.getState();

    // Track final storage levels (updated each hour, last hour = final)
    this.results.finalHTESLevel = levels.htes * 100;
    this.results.finalLTESLevel = levels.ltes * 100;
    this.results.finalBatteryLevel = levels.battery * 100;

    this.results.hourlyData.push(hourData);
    this.currentHour++;

    // Return null hvis vi har nået maks timer
    if (this.currentHour > CONSTANTS.SIMULATION.totalHours) {
      return null;
    }

    return hourData;
  }
  
  getAveragePrices() {
    return {
      operationalGrid: this.results.operationalGridPurchased > 0 
        ? this.results.operationalGridCost / this.results.operationalGridPurchased 
        : 0,
      operationalSolar: this.results.operationalSolarPurchased > 0 
        ? this.results.operationalSolarCost / this.results.operationalSolarPurchased 
        : 0,
      wasteHeat: CONSTANTS.FACTORY_WASTE_HEAT_PRICE,
      // Brug kun operationelle omkostninger for rå DKK/MWh (ekskl. arbitrage)
      overall: this.results.anPlantProduction > 0
        ? (this.results.operationalGridCost + this.results.operationalSolarCost + this.results.operationalFactoryWasteHeatCost) / this.results.anPlantProduction
        : 0
    };
  }
  
  getFinalStats() {
    // CRITICAL: Calculate ACTUAL operational electricity needs
    // An-værk production needs = Total An-værk - Waste Heat contribution
    const actualElectricityForAnPlant = Math.max(0, 
      this.results.anPlantProduction - this.results.wasteHeatAccepted
    );
    
    // Recalculate operational purchases based on ACTUAL needs
    const totalElectricityPurchased = this.results.operationalGridPurchased + 
                                      this.results.operationalSolarPurchased +
                                      this.results.arbitrageGridPurchased +
                                      this.results.arbitrageSolarPurchased;
    
    // Calculate proportion of operational vs arbitrage
    const operationalProportion = totalElectricityPurchased > 0 ? 
      actualElectricityForAnPlant / totalElectricityPurchased : 0;
    
    // Adjust operational purchases to reflect ACTUAL An-værk needs
    const adjustedOperationalGrid = this.results.operationalGridPurchased * operationalProportion;
    const adjustedOperationalSolar = this.results.operationalSolarPurchased * operationalProportion;
    const adjustedOperationalGridCost = this.results.operationalGridCost * operationalProportion;
    const adjustedOperationalSolarCost = this.results.operationalSolarCost * operationalProportion;
    
    // Calculate average prices
    const totalGridPurchased = this.results.operationalGridPurchased + this.results.arbitrageGridPurchased;
    const totalGridCost = this.results.operationalGridCost + this.results.arbitrageGridCost;
    const totalSolarPurchased = this.results.operationalSolarPurchased + this.results.arbitrageSolarPurchased;
    const totalSolarCost = this.results.operationalSolarCost + this.results.arbitrageSolarCost;
    
    return {
      ...this.results,
      // Override operational values with ADJUSTED values based on actual An-værk needs
      operationalGridPurchased: adjustedOperationalGrid,
      operationalSolarPurchased: adjustedOperationalSolar,
      operationalGridCost: adjustedOperationalGridCost,
      operationalSolarCost: adjustedOperationalSolarCost,
      
      // Add calculated values for UI
      actualElectricityForAnPlant: actualElectricityForAnPlant,
      
      // Add calculated average prices for UI
      avgGridPrice: totalGridPurchased > 0 ? totalGridCost / totalGridPurchased : 0,
      avgSolarPrice: totalSolarPurchased > 0 ? totalSolarCost / totalSolarPurchased : 0,
      avgWasteHeatPrice: CONSTANTS.FACTORY_WASTE_HEAT_PRICE || 150,
      // FIX: Calculate battery charge price from arbitrage data
      avgBatteryChargePrice: this.results.batteryChargeAmount > 0 ? this.results.arbitrageGridCost / this.results.batteryChargeAmount : 0,
      // FIX: Calculate battery discharge price from revenue
      avgBatteryDischargePrice: this.results.batteryDischargeAmount > 0 ? this.results.batteryArbitrageRevenue / this.results.batteryDischargeAmount : 0,
      
      // Add total cost for UI
      totalCost: this.results.totalOperationalCost,
      
      // Add demand coverage
      demandCoverage: 100,  // Always 100% with emergency mode
      
      // Add monthly heater tracking for balance market
      monthlyHeaterAvailableHours: this.monthlyHeaterTracking.monthlyAvailableHours,
      monthlyHeaterUsedHours: this.monthlyHeaterTracking.monthlyUsedHours,
      monthlyHeaterUsedCapacity: this.monthlyHeaterTracking.monthlyUsedCapacity,
      unmetDemandHours: 0,
      
      anAbValidation: {
        expectedAnPlant: CONSTANTS.ANNUAL_DEMAND.anPlant,
        actualAnPlant: this.results.anPlantProduction,
        difference: this.results.anPlantProduction - CONSTANTS.ANNUAL_DEMAND.anPlant,
        coveragePercent: (this.results.anPlantProduction / CONSTANTS.ANNUAL_DEMAND.anPlant) * 100
      },
      batteryCycles: {
        chargeCycles: this.batteryDailyTracking.dailyChargeRecord.filter(d => d).length,
        dischargeCycles: this.batteryDailyTracking.dailyDischargeRecord.filter(d => d).length,
        totalChargedMWh: this.batteryDailyTracking.totalChargedMWh,
        totalDischargedMWh: this.batteryDailyTracking.totalDischargedMWh,
        expectedCycles: 366,  // Leap year
        cycleUtilization: (this.batteryDailyTracking.dailyChargeRecord.filter(d => d).length / 366) * 100,
        // Debug: Find which days are missing cycles
        missedChargeDays: this.batteryDailyTracking.dailyChargeRecord
          .map((charged, day) => !charged ? day : null)
          .filter(d => d !== null)
          .slice(0, 10), // Show first 10 missed days
        missedDischargeDays: this.batteryDailyTracking.dailyDischargeRecord
          .map((discharged, day) => !discharged ? day : null)
          .filter(d => d !== null)
          .slice(0, 10) // Show first 10 missed days
      },

      // HTES Arbitrage (el + varme produktion via turbine)
      htesArbitrage: {
        totalChargeMWh: this.htesArbitrageTracking.totalChargeMWh,
        totalChargeCost: this.htesArbitrageTracking.totalChargeCost,
        avgChargePrice: this.htesArbitrageTracking.totalChargeMWh > 0
          ? this.htesArbitrageTracking.totalChargeCost / this.htesArbitrageTracking.totalChargeMWh
          : 0,
        // El produktion
        totalElectricityProduced: this.htesArbitrageTracking.totalDischargeMWh,
        totalElRevenue: this.htesArbitrageTracking.totalDischargeRevenue,
        avgElSalePrice: this.htesArbitrageTracking.totalDischargeMWh > 0
          ? this.htesArbitrageTracking.totalDischargeRevenue / this.htesArbitrageTracking.totalDischargeMWh
          : 0,
        // Varme produktion
        totalHeatProduced: this.htesArbitrageTracking.totalHeatProduced,
        totalHeatSavings: this.htesArbitrageTracking.totalHeatSavings,
        // Total værdi og profit
        totalValue: this.htesArbitrageTracking.totalDischargeRevenue + this.htesArbitrageTracking.totalHeatSavings,
        netProfit: (this.htesArbitrageTracking.totalDischargeRevenue + this.htesArbitrageTracking.totalHeatSavings)
                   - this.htesArbitrageTracking.totalChargeCost,
        cycles: this.htesArbitrageTracking.cycleCount,
        // Gennemsnitspris for el-produktion (hvad koster det at producere 1 MWh el via HTES)
        avgPriceFor100PercentEl: this.htesArbitrageTracking.totalDischargeMWh > 0
          ? this.htesArbitrageTracking.totalChargeCost / this.htesArbitrageTracking.totalDischargeMWh
          : 0,
        // Effektiv kostpris efter varme-kredit
        effectiveCostPerMWhEl: this.htesArbitrageTracking.totalDischargeMWh > 0
          ? (this.htesArbitrageTracking.totalChargeCost - this.htesArbitrageTracking.totalHeatSavings)
            / this.htesArbitrageTracking.totalDischargeMWh
          : 0,
      }
    };
  }
}

export default SimulationEngineV2;